import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useParams } from "react-router-dom";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, arrayInString } from "@utils/utilsApi";
import ToastCst from "@components/ToastCst";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import ButtonBackPage from "@components/ButtonBackPage";
import { ColumnsType } from "src/interfaces/Common";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const AssociationTopicCourses: React.FC = () => {
  const { t } = useTranslation(["topics-page"]);
  const { idTopic, id, idInitiative } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.initiative.topics"] + idTopic}`;

  const [coursesAssociation, setCoursesAssoiation] = useState<any>(undefined);
  const [columnsMerged, setColumnsMerged] = useState<ColumnsType[]>();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    key?: string | number;
  }>();

  const { dataDetail: dataDetailTopic, mutate } = useFetchDetail(
    ServicesURL.topics + `/${idInitiative}/topics`,
    idTopic || "",
    PagesID["corporate.initiative.topics"],
  );

  useEffect(() => {
    if (
      dataDetailTopic &&
      dataDetailTopic.lo_list &&
      dataDetailTopic.lo_list?.length > 0
    ) {
      let obj = {};
      dataDetailTopic?.lo_list?.forEach((element: any) => {
        if (element) {
          obj = {
            ...obj,
            [element]: true,
          };
        }
      });
      setCoursesAssoiation(obj);
    } else if (
      !!coursesAssociation &&
      dataDetailTopic.lo_list &&
      dataDetailTopic.lo_list?.length === 0
    ) {
      setCoursesAssoiation(undefined);
    }
  }, [dataDetailTopic]);

  const { columns, dataTable, actionsTopToolbar } = useFetchTable(
    `${ServicesURL["learning-catalogue"]}/${id}/learning-objects/v3/courses?initiative=${idInitiative}&is_standalone=true`,
    PagesID["corporate.catalog.courses"],
    filters[filterId],
  );

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID["corporate.catalog.courses"],
  );

  const { trigger: triggerCategoryAssociation } = useSWRMutation(
    ServicesURL.catalogTopicAssociation
      .replace(":idCorporate", `${id}`)
      .replace(":initiativeId", `${idInitiative}`),
    (url: string, { arg }: { arg: { id: string } }) => {
      const formattedUrl = url.replace(":idLO", `${arg.id}`);
      return fetcher<any>(`${formattedUrl}`, MethodType.PUT, {
        id: idTopic && +idTopic,
      });
    },
  );

  const { trigger: triggerTopicDisassociation } = useSWRMutation(
    ServicesURL.catalogTopicDisassociation
      .replace(":idCorporate", `${id}`)
      .replace(":initiativeId", `${idInitiative}`)
      .replace(":idTopic", `${idTopic}`),
    (url: string, { arg }: { arg: { id: string } }) => {
      const formattedUrl = url.replace(":idLO", `${arg.id}`);
      return fetcher<any>(`${formattedUrl}`, MethodType.DELETE);
    },
  );

  const handleRowSelection = async (courses: any) => {
    const associationIds =
      (coursesAssociation !== undefined && Object.keys(coursesAssociation)) ||
      [];
    const courseIds = Object.keys(courses);
    if (associationIds?.length > courseIds?.length) {
      // delete
      const elementDeleted = associationIds.find(
        (elem: any) => !courseIds.includes(elem),
      );
      const postObject: any = {
        id: Number(elementDeleted),
      };
      try {
        await triggerTopicDisassociation(postObject);
        setOpenToast({
          s: StatusEnum.OK,
          message: t("removed", { ns: "toast-message" }),
          key: elementDeleted,
        });
      } catch (error) {
        console.error(error);
      }
    } else if (associationIds?.length < courseIds?.length) {
      // add
      const elementAdded = courseIds.find(
        (elem: any) => !associationIds.includes(elem),
      );
      const postObject: any = {
        id: Number(elementAdded),
      };
      try {
        await triggerCategoryAssociation(postObject);
        setOpenToast({
          s: StatusEnum.OK,
          message: t("added", { ns: "toast-message" }),
          key: elementAdded,
        });
      } catch (error) {
        console.error(error);
      }
    }
    mutate && mutate(courses);
  };

  const customActionsTopToolbar =
    actionsTopToolbar &&
    actionsTopToolbar.filter((elem: any) => elem.type !== "add");

  const handleCloseToastMessage = () => {
    setOpenToast(undefined);
  };

  useEffect(() => {
    if (!columnsMerged) {
      if (columns) {
        setColumnsMerged([
          ...(columns.map((el: ColumnsType) => {
            switch (el.accessorKey) {
              case "initiative": {
                return {
                  ...el,
                  Cell: ({ cell }: any) => {
                    const value = cell.getValue();
                    return arrayInString(value, "label");
                  },
                };
              }
              default:
                return el;
            }
          }) || []),
        ]);
      }
    }
  }, [columns, columnsMerged]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-groups.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${dataDetail?.name} - ${t("association-courses.hero-title")}`}
          description={t("association-courses.hero-description")}
        />
        <ButtonBackPage />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={PagesID["corporate.initiative.topics"] + idTopic}
              data={dataTable?.output}
              columns={columnsMerged || []}
              configurationTopToolbar={customActionsTopToolbar}
              enableRowSelection={true}
              enableRowActions={false}
              selectedRows={coursesAssociation}
              onRowSelectionChange={(courses: any) => {
                handleRowSelection(courses);
              }}
              getRowId={(originalRow: any) => originalRow?.id}
              totalPages={dataTable?.metadata?.total_elements}
              noSummarySelected={true}
              modAssociationEnabled={true}
            />
          </Box>
        )}
      </Container>
      <ToastCst
        key={(openToast && openToast.key) || ""}
        message={(openToast && openToast.message) || ""}
        open={!!openToast}
        handleClose={handleCloseToastMessage}
      />
    </AnimationFadeIn>
  );
};

export default AssociationTopicCourses;
